import React, { Fragment, useContext, useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Flex, Inline, xcss, Box } from '@atlaskit/primitives';
import Button, { IconButton } from '@atlaskit/button/new';
import ClockIconMigration from '@atlaskit/icon/core/migration/clock--emoji-frequent';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/close';
import { token } from '@atlaskit/tokens';

import { SidePanel, SidePanelFooter } from '@confluence/side-panel';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import {
	WHITEBOARD_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
	WHITEBOARD_VERSION_HISTORY_LOAD_RESTORE_MODAL,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';

import type { ContentVersionHistoryQuery_contentVersionHistory_nodes as VersionHistoryNodeType } from '../graphql/__types__/ContentVersionHistoryQuery';
import type { WhiteboardEventService } from '../services/whiteboard-event-service';

import { useVersionHistory } from './useVersionHistory';
import { VersionHistoryContent } from './VersionHistoryContent';
import { VersionHistoryLoadingPlaceholder } from './VersionHistoryLoadingPlaceholder';
import { RestoreContentModal } from './RestoreModal/RestoreContentModal';
import { useVersionHistoryAnalytics } from './useVersionHistoryAnalytics';

const headerTitleStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

const i18n = defineMessages({
	versionHistoryTitle: {
		id: 'whiteboard-common.title',
		defaultMessage: 'Version History',
		description: 'Title for version history component',
	},
	versionHistoryIconLabel: {
		id: 'whiteboard-common.icon.label',
		defaultMessage: 'Frequent clock icon',
		description: 'aria-label for frequent clock icon',
	},
	versionHistoryCloseButtonLabel: {
		id: 'whiteboard-common.close.button.icon.label',
		defaultMessage: 'Close button',
		description: 'aria-label for close button',
	},
	currentVersionTitle: {
		id: 'whiteboard-common.current.version.title',
		defaultMessage: 'Current version',
		description: 'Title for current version section',
	},
	autoSavedVersionsTitle: {
		id: 'whiteboard-common.auto-saved.version.title',
		defaultMessage: 'Autosaved versions',
		description: 'Title for current version section',
	},
	restoreWhiteboardLabel: {
		id: 'whiteboard-common.restore.whiteboard.label',
		defaultMessage: 'Restore as new whiteboard',
		description: 'aria label for restore as new whiteboard button',
	},
	restoreWhiteboardButtonText: {
		id: 'whiteboard-common.restore.whiteboard.button.text',
		defaultMessage: 'Restore as new whiteboard',
		description: 'Button text to restore whiteboard',
	},
});

type WhiteboardVersionHistoryProps = {
	headerHeight?: number;
	contentId: string;
	displayTopBorder?: boolean;
	panelTop?: number;
	eventService: WhiteboardEventService;
	setSelectedVersionId: (versionId: string | null) => void;
};

const WhiteboardVersionHistoryComponent = ({
	contentId,
	headerHeight = 0,
	displayTopBorder,
	panelTop = 0,
	eventService,
	setSelectedVersionId,
}: WhiteboardVersionHistoryProps) => {
	const { hideSidePanel } = useSidePanelStateActions();
	const { formatMessage } = useIntl();
	const [showRestoreModal, setShowRestoreModal] = useState(false);
	const { onVersionClick, onVersionHistoryMenuOpen, onRestoreModalClose } =
		useVersionHistoryAnalytics();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		autosavedVersions,
		currentVersion,
		loading,
		loadMoreVersions,
		hasNextPage,
		cursor,
		error,
		refetch,
	} = useVersionHistory({
		contentId,
		contentType: 'whiteboard',
	});

	// current version is selected by default
	const [selectedVersion, setSelectedVersion] = useState<VersionHistoryNodeType | null>(
		currentVersion,
	);

	useEffect(() => {
		// succeed side panel experience on render
		experienceTracker.succeed({
			name: WHITEBOARD_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
		});
	}, [experienceTracker]);

	useEffect(() => {
		if (!selectedVersion) {
			setSelectedVersion(currentVersion);
		}
	}, [currentVersion, setSelectedVersion, selectedVersion]);

	useEffect(() => {
		onVersionHistoryMenuOpen();
	}, [onVersionHistoryMenuOpen]);

	const handleClose = useCallback(() => {
		onRestoreModalClose();
		setShowRestoreModal(false);
	}, [onRestoreModalClose]);

	const handleVersionClick = useCallback(
		(version: VersionHistoryNodeType) => {
			setSelectedVersion(version);
			onVersionClick(version);
			setSelectedVersionId(version?.contentProperties?.latest?.externalVersionId || null);
		},
		[onVersionClick, setSelectedVersionId],
	);

	// current version does not return externalVersionId
	const isRestoreButtonDisabled =
		!!error || !Boolean(selectedVersion?.contentProperties?.latest?.externalVersionId);

	const handleRestoreVersionClick = useCallback(() => {
		experienceTracker.start({
			name: WHITEBOARD_VERSION_HISTORY_LOAD_RESTORE_MODAL,
		});
		setShowRestoreModal(true);
	}, [experienceTracker]);

	return (
		<Fragment>
			<SidePanel
				headerHeight={headerHeight}
				displayTopBorder={displayTopBorder}
				panelTop={panelTop}
			>
				{loading ? (
					<VersionHistoryLoadingPlaceholder />
				) : (
					<Fragment>
						<Box
							backgroundColor="elevation.surface"
							paddingInlineStart="space.300"
							paddingInlineEnd="space.150"
							paddingBlock="space.150"
						>
							<Flex direction="row" justifyContent="space-between" alignItems="center">
								<Inline space="space.100" xcss={headerTitleStyles}>
									<ClockIconMigration
										label={formatMessage(i18n.versionHistoryIconLabel)}
										color={token('color.icon')}
										spacing="none"
									/>
									<Heading size="small" as="h5">
										<FormattedMessage {...i18n.versionHistoryTitle} />
									</Heading>
								</Inline>
								<IconButton
									appearance="subtle"
									icon={CloseIcon}
									onClick={hideSidePanel}
									label={formatMessage(i18n.versionHistoryCloseButtonLabel)}
								/>
							</Flex>
						</Box>
						<VersionHistoryContent
							currentVersion={currentVersion}
							autosavedVersions={autosavedVersions}
							hasNextPage={hasNextPage}
							loadMoreVersions={loadMoreVersions}
							cursor={cursor}
							error={error}
							refetch={refetch}
							loading={loading}
							handleVersionClick={handleVersionClick}
							selectedVersion={selectedVersion}
							eventService={eventService}
						/>
						<SidePanelFooter>
							<Button
								appearance="primary"
								aria-label={formatMessage(i18n.restoreWhiteboardLabel)}
								shouldFitContainer
								onClick={handleRestoreVersionClick}
								isDisabled={isRestoreButtonDisabled}
							>
								<FormattedMessage {...i18n.restoreWhiteboardButtonText} />
							</Button>
						</SidePanelFooter>
					</Fragment>
				)}
			</SidePanel>
			{showRestoreModal && selectedVersion && (
				<RestoreContentModal onClose={handleClose} selectedVersion={selectedVersion} />
			)}
		</Fragment>
	);
};

export const WhiteboardVersionHistory = withErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(WhiteboardVersionHistoryComponent);
